import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { getFromQueryOrStorage, getSplitTestEnabled } from 'src/app/app/app.constants';
import { AppState } from 'src/app/store/state';
import { environment } from 'src/environments/environment';
import { AuthUser, OAuthCompany, SsoProviderType } from 'src/models';
import { ApiService } from '../api.service';
import { AuthService, CredentialAuthResponse, EditProfileProps, LoginResult, SsoOtpResponse, TokenResult, TokenScope } from './auth.service';
import { CookieAuthMigrateService, CookieAuthService } from './cookie-auth.service';
import { TokenAuthService } from './token-auth.service';

/**
 * AutoAuthService is a service that automatically switches between TokenAuthService and CookieAuthService based on the auth mode.
 * If the user is currently logged in, it will use the service user is logged in with.
 * Otherwise, it will get the mode either from the environment variables or from the URL authMode query parameter. (e.g. `?authMode=v2`)
 */
@Injectable()
export class AutoAuthService extends AuthService {
  private authModeFromQuery = getFromQueryOrStorage('authMode');

  get effectiveService() {
    const resolvedAuthMode = this.authModeFromQuery;
    if (resolvedAuthMode === 'v1') return this.tokenAuth;

    const hasToken = this.store.selectSnapshot((st: AppState) => st.auth.token);
    const preferAuthV2 = resolvedAuthMode === 'v2' || getSplitTestEnabled(environment.authV2SplitTestRatio, 'auth-v2') || environment.authMode === 'v2';

    if (this.store.selectSnapshot((st: AppState) => st.auth.cookieAuth)) return this.cookieAuth;
    if (hasToken && preferAuthV2) return this.cookieAuthMigrate;
    if (preferAuthV2) return this.cookieAuth;
    if (hasToken) return this.tokenAuth;

    return this.tokenAuth;
  }

  constructor(
    private tokenAuth: TokenAuthService,
    private cookieAuth: CookieAuthService,
    private cookieAuthMigrate: CookieAuthMigrateService,
    store: Store,
    api: ApiService,
  ) {
    super(api, store);
  }

  get version() {
    return this.effectiveService.version;
  }

  refreshToken(): Promise<unknown> {
    return this.effectiveService.refreshToken();
  }

  login(email: string, password: string, totpCode?: string): Promise<LoginResult> {
    return this.effectiveService.login(email, password, totpCode);
  }

  logout(): Promise<unknown> {
    this.onLogout.emit();
    return this.effectiveService.logout();
  }

  auth(): Promise<AuthUser> {
    return this.effectiveService.auth();
  }

  resetPassword(token: string, password: string): Promise<unknown> {
    return this.effectiveService.resetPassword(token, password);
  }

  forgotPassword(email: string): Promise<unknown> {
    return this.effectiveService.forgotPassword(email);
  }

  getScopedToken(scope: TokenScope): Promise<string> {
    return this.effectiveService.getScopedToken(scope);
  }

  getTokenHeader(token: string): Record<string, string> {
    return this.effectiveService.getTokenHeader(token);
  }

  oauthLogin(provider: SsoProviderType, token: string): Promise<{ token: string }> {
    return this.effectiveService.oauthLogin(provider, token);
  }

  findCompaniesByOTP(email: string, issuer: string, otp: string): Promise<OAuthCompany[]> {
    return this.effectiveService.findCompaniesByOTP(email, issuer, otp);
  }

  isSsoOtpEnabled(email: string): Promise<SsoOtpResponse> {
    return this.effectiveService.isSsoOtpEnabled(email);
  }

  sendOtpRequest(email: string): Promise<unknown> {
    return this.effectiveService.sendOtpRequest(email);
  }

  register(email: string, name: string, password: string, params?: any): Promise<TokenResult | undefined> {
    return this.effectiveService.register(email, name, password, params);
  }

  editProfile(properties: EditProfileProps): Promise<TokenResult> {
    return this.effectiveService.editProfile(properties);
  }

  validateMagicLinkToken(token: string): Promise<any> {
    return this.effectiveService.validateMagicLinkToken(token);
  }

  oauthGoogleLogin(token: string): Promise<CredentialAuthResponse> {
    return this.effectiveService.oauthGoogleLogin(token);
  }
}
